import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import { IconButton } from "../../../components/button/Button";
import SearchBar from "../../../components/searchBar/SearchBar";
import { isEmpty, isNil } from "lodash-es";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import { useTranslation } from "../../../state/i18n";
import { softContains } from "../../../utils/stringUtil";
import VariableService from "../../../services/VariableService";
import { Menu } from "@ark-ui/solid";
import type { VariableType } from "../../../variables/types";
import { VARIABLE_TYPES } from "../../../variables/types";
import { DestructiveModal } from "../../../components/modal/Modal";
import type VariableId from "../../../variables/VariableId";
import toVariableDefinition from "../../../variables/toVariableDefinition";
import DynamicValueListItem from "../../../components/menu/listItems/DynamicValueListItem";
import { useModalManager } from "../../../components/modal/ModalManager";
import FeatureService from "../../../services/FeatureService";

const VariablesContent: VoidComponent = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const [searchQuery, setSearchQuery] = createSignal("");
  const variables = createMemo(() => VariableService.getVariables());

  const filteredVariables = createMemo(() =>
    variables().filter(
      (variable) => softContains(variable.name, searchQuery()) || softContains(variable.dataName, searchQuery()),
    ),
  );

  const EmptyState = {
    NoVariables: () => (
      <div class="flex justify-center">
        <div class="flex w-66 flex-col items-center gap-1 px-3 py-10 text-center">
          <h3 class="text-header-lg text-primary">{t(($) => $.VARIABLES_NO_VARIABLES_TITLE)}</h3>
          <p class="text-base-regular text-secondary">{t(($) => $.VARIABLES_NO_VARIABLES_DESCRIPTION)}</p>
        </div>
      </div>
    ),
    Filtered: () => (
      <div class="flex justify-center">
        <div class="flex w-66 flex-col gap-2 px-3 py-10 text-center">
          <h3 class="text-header-lg text-primary">{t(($) => $.VARIABLES_NO_RESULTS_TITLE)}</h3>
          <p class="text-base-regular text-secondary">{t(($) => $.VARIABLES_NO_RESULTS_DESCRIPTION)}</p>
        </div>
      </div>
    ),
  };

  return (
    <section class="flex h-full flex-col">
      <header class="mb-4 border-b border-subtle pb-3" data-testid="variables-header">
        <div class="mb-3 flex items-center gap-3">
          <h2 class="text-header-xl text-primary">{t(($) => $.VARIABLES_TITLE)}</h2>
          <CreateVariableButton onSelect={() => setSearchQuery("")} />
        </div>

        <div class="flex gap-4">
          <div class="grow">
            <SearchBar
              query={searchQuery()}
              onQueryChange={(value) => {
                setSearchQuery(value);
              }}
              placeholder={t(($) => $.VARIABLES_SEARCH_PLACEHOLDER)}
            />
          </div>
        </div>
      </header>
      <article aria-label={t(($) => $.VARIABLES_LIST_ARIA_LABEL)} class="flex-1 overflow-hidden">
        <Show when={!isEmpty(variables())} fallback={<EmptyState.NoVariables />}>
          <div class="flex h-full flex-col gap-2 overflow-y-auto pb-15">
            <For each={filteredVariables()} fallback={<EmptyState.Filtered />}>
              {(variable) => {
                const definition = toVariableDefinition(variable.type);
                return (
                  <definition.ListItem
                    uid={variable.uid}
                    onDelete={(id) => open({ content: () => <DeleteModal idToDelete={id} /> })}
                    disabled={!FeatureService.hasFeature("FORM_VARIABLES")}
                  />
                );
              }}
            </For>
          </div>
        </Show>
      </article>
    </section>
  );
};

type DeleteModalProps = {
  idToDelete: VariableId | undefined;
};
const DeleteModal: VoidComponent<DeleteModalProps> = (props) => {
  const { t } = useTranslation();

  return (
    <DestructiveModal
      title={t(($) => $.VARIABLES_DELETE_VARIABLE_TITLE)}
      description={t(($) => $.VARIABLES_DELETE_VARIABLE_DESCRIPTION)}
      confirmLabel={t(($) => $.DELETE_BUTTON)}
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      onConfirm={() => {
        if (!isNil(props.idToDelete)) {
          VariableService.deleteVariable(props.idToDelete);
        }
      }}
    />
  );
};

type CreateVariableButtonProps = { onSelect: () => void };
const CreateVariableButton: VoidComponent<CreateVariableButtonProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Menu.Root
      onSelect={({ value }) => {
        const variable = VariableService.createVariable(value as VariableType);
        VariableService.addVariable(variable);
        props.onSelect();
      }}
    >
      <Menu.Trigger
        asChild={(props) => (
          <IconButton
            {...props}
            size="medium"
            variant="light"
            icon="plus"
            aria-label={t(($) => $.VARIABLES_CREATE_ARIA_LABEL)}
          />
        )}
      />
      <Menu.Positioner>
        <Menu.Content class="z-10 -my-1 max-h-86 min-h-12 max-w-170 min-w-66 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none">
          <For each={VARIABLE_TYPES}>
            {(type) => {
              const definition = toVariableDefinition(type);
              return (
                <Menu.Item
                  value={definition.type}
                  asChild={(arkProps) => (
                    <DynamicValueListItem
                      icon={{ name: definition.icon, class: "icon-primary bg-surface-subtle" }}
                      label={t(definition.titleKey)}
                      htmlProps={arkProps()}
                    />
                  )}
                />
              );
            }}
          </For>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};

const VariablesContentWithErrorBoundary = withErrorBoundary(VariablesContent);

export default VariablesContentWithErrorBoundary;
